<script lang="ts">
	import type { Merge } from 'type-fest'
	import type { i18n } from 'types/common'

	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import { graphql, type VendorPurchaseOrders$result } from '$houdini'
	import { datesFromRange } from '@isoftdata/utility-date-time'
	import { endOfDay, parseISO, startOfDay } from 'date-fns'
	import { onMount, type ComponentProps } from 'svelte'
	import { hasPermission } from 'utility/permission'

	import Button from '@isoftdata/svelte-button'
	import DateRange from '@isoftdata/svelte-date-range'
	import ReportJobModal from 'components/ReportJobModal.svelte'
	import Table, { type Column, Td } from '@isoftdata/svelte-table'

	import { getContext } from 'svelte'

	type PurchaseOrderStatus = 'Open' | 'Prices Finalized' | 'Ordered' | 'Done Receiving' | 'Finalized' | 'Void'
	type VendorPurchaseOrder = Merge<
		VendorPurchaseOrders$result['vendor']['purchaseOrders']['items'][number],
		{ shipTo: string; status: PurchaseOrderStatus }
	>

	interface Props {
		vendorId: number | null //it could be a new vendor
	}

	let { vendorId }: Props = $props()

	let vendorPurchaseOrders: Array<VendorPurchaseOrder> = $state([])
	let selectedPurchaseOrder: VendorPurchaseOrder | null = $state(null)
	let isLoadingPurchaseOrders: boolean = $state(false)
	let reportJobModalComponent: ReportJobModal = $state()!

	let dates: ComponentProps<typeof DateRange>['dates'] = $state(undefined)
	let range: ComponentProps<typeof DateRange>['range'] = $state('Last 90 Days')
	let currentPageNumber = $state(1)
	let totalItemsCount: number | undefined = $state(undefined)

	const { t: translate } = getContext<i18n>('i18next')

	const purchaseOrderHistoryColumn: Array<Column<VendorPurchaseOrder>> = [
		{ property: 'id', name: translate('common:POnum', 'PO #'), numeric: true },
		{ property: 'date', name: translate('common:date', 'Date') },
		{ property: 'subtotal', name: translate('common:subtotal', 'Subtotal'), numeric: true, sortType: 'ALPHA_NUM' },
		{ property: 'tax', name: translate('common:tax', 'Tax'), numeric: true, sortType: 'ALPHA_NUM' },
		{ property: 'total', name: translate('common:total', 'Total'), numeric: true, sortType: 'ALPHA_NUM' },
		{ property: 'shipTo', name: translate('common:customer', 'Customer') },
		{ property: 'salesPerson[name]', name: translate('common:salesperson', 'Salesperson') },
		{ property: 'store[id]', name: translate('common:storeNumber', 'Store #'), numeric: true },
		{
			property: 'receivedPercent',
			name: translate('home.purchaseOrder.percentReceivedColumn', '% Received'),
			numeric: true,
		},
		{ property: 'status', name: translate('common:poStatus', 'PO status') },
		{ property: 'internalComments', name: translate('common:internalComments', 'Internal Comments') },
	]

	const lineItemColumn: Array<Column<VendorPurchaseOrder['lines'][number]>> = [
		{ property: 'lookup', name: translate('common:part', 'Part') },
		{ property: 'quantity', name: translate('common:qty', 'Qty'), numeric: true },
		{ property: 'quantityReceived', name: translate('common:received', 'Received'), numeric: true },
		{ property: 'description', name: translate('common:description', 'Description') },
		{ property: 'price', name: translate('common:price', 'Price'), numeric: true, sortType: 'ALPHA_NUM' },
		{ property: 'total', name: translate('common:total', 'Total'), numeric: true, sortType: 'ALPHA_NUM' },
	]

	let perPageCount: number = 6

	const vendorPurchaseOrdersQuery = graphql(`
		query VendorPurchaseOrders($vendorId: UInt!, $filter: PurchaseOrderFilter, $pagination: PaginationOptions) {
			vendor(id: $vendorId) {
				purchaseOrders(filter: $filter, pagination: $pagination) {
					items {
						id
						date
						doneReceiving
						finalized
						internalComments
						ordered
						pricesFinalized
						purchaseOrderId
						receivedPercent
						subtotal
						tax
						total
						void
						customer {
							id
							companyName
							contactName
						}
						salesPerson {
							id
							name
						}
						shippingAddress {
							company
							contact
						}
						store {
							id
							name
						}
						lines {
							id
							description
							lookup
							price
							total
							quantity
							quantityReceived
						}
					}
					pageInfo {
						pageNumber
						pageSize
					}
					totalItems
				}
			}
		}
	`)

	// get vendor purchase orders with formatted status and shipping destination
	async function getVendorPurchaseOrders(
		filter: { dates?: { from?: string | null | undefined; to?: string | null | undefined } | null | undefined },
		pageNumber: number,
		vendorId: number,
	) {
		isLoadingPurchaseOrders = true
		const { data } = await vendorPurchaseOrdersQuery.fetch({
			variables: {
				vendorId,
				filter:
					filter.dates?.from && filter.dates?.to
						? {
								date: {
									gte: startOfDay(parseISO(filter.dates.from)),
									lte: endOfDay(parseISO(filter.dates.to)),
								},
							}
						: {},
				pagination: {
					pageSize: 6,
					pageNumber,
				},
			},
		})

		if (!data) {
			throw new Error(translate('vendor.failedToFetchVendorPurchaseOrders', 'Failed to fetch vendor purchase orders'))
		}
		vendorPurchaseOrders =
			data.vendor?.purchaseOrders?.items.map(purchaseOrder => ({
				...purchaseOrder,
				shipTo:
					purchaseOrder.shippingAddress?.company ||
					purchaseOrder.shippingAddress?.contact ||
					translate('common:notSlashAcceptable', 'N/A'),
				status: getPOStatus(purchaseOrder).status,
			})) ?? []
		selectedPurchaseOrder = null

		currentPageNumber = data.vendor?.purchaseOrders?.pageInfo.pageNumber ?? 1
		totalItemsCount = data.vendor?.purchaseOrders?.totalItems
		isLoadingPurchaseOrders = false
	}

	function getPOStatus(purchaseOrder: VendorPurchaseOrders$result['vendor']['purchaseOrders']['items'][number]): {
		status: PurchaseOrderStatus
		textColor: string
	} {
		if (purchaseOrder.void) {
			return { status: 'Void', textColor: 'danger' }
		} else if (purchaseOrder.finalized) {
			return { status: 'Finalized', textColor: 'success' }
		} else if (purchaseOrder.doneReceiving) {
			return { status: 'Done Receiving', textColor: 'primary' }
		} else if (purchaseOrder.ordered) {
			return { status: 'Ordered', textColor: 'warning' }
		} else if (purchaseOrder.pricesFinalized) {
			return { status: 'Prices Finalized', textColor: 'info' }
		} else {
			return { status: 'Open', textColor: '' }
		}
	}

	onMount(async () => {
		if (vendorId && range && range !== 'Custom') {
			dates = datesFromRange(range)
			await getVendorPurchaseOrders({ dates }, 1, vendorId)
		}
	})
</script>

{#if !hasPermission('PURCHASE_ORDERS_CAN_VIEW')}
	<div class="alert alert-warning text-center">
		{translate(
			'vendor.youDoNotHavePermissionToViewPurchaseOrderHistoryPleaseContactYourAdminForFurtherAssistance',
			'You do not have permission to view purchase order history. Please contact your admin for further assistance.',
		)}
	</div>
{:else}
	<div class="card">
		<div class="card-header">
			<h4 class="mb-0">{translate('vendor.purchaseOrderHistory', 'Purchase Order History')}</h4>
		</div>
		<div class="card-body">
			<div class="mb-1">
				<DateRange
					colClass="col-md-3"
					disabled={isLoadingPurchaseOrders || !vendorId}
					bind:dates
					bind:range
					change={async () => {
						if (vendorId) {
							await getVendorPurchaseOrders({ dates }, currentPageNumber, vendorId)
						}
					}}
				/>
			</div>
			{#if isLoadingPurchaseOrders}
				<div class="text-center">
					<i class="fas fa-spinner fa-spin fa-2x"></i>
				</div>
			{:else}
				<Table
					responsive
					stickyHeader
					columnHidingEnabled
					columnResizingEnabled
					{perPageCount}
					{currentPageNumber}
					{totalItemsCount}
					tableId="purchase-order-history"
					localStorageKey="purchase-order-history"
					columns={purchaseOrderHistoryColumn}
					rows={vendorPurchaseOrders}
					pageChange={({ pageNumber }) => {
						currentPageNumber = pageNumber
						if (vendorId) {
							getVendorPurchaseOrders({ dates }, currentPageNumber, vendorId)
						}
					}}
				>
					{#snippet noRows({ visibleColumnsCount })}
						<tr>
							<td
								class="text-center"
								colspan={visibleColumnsCount}
							>
								{translate('vendor.noPurchaseOrderFound', 'No purchase order found')}
							</td>
						</tr>
					{/snippet}
					{#snippet children({ row })}
						<tr
							class="cursor-pointer"
							class:table-primary={selectedPurchaseOrder === row}
							onclick={() => {
								selectedPurchaseOrder = row
							}}
						>
							<Td property="id">
								{row.id}
							</Td>
							<Td property="date">
								{row.date.toLocaleDateString()}
							</Td>
							<Td property="subtotal">
								{formatCurrency(row.subtotal)}
							</Td>
							<Td property="tax">
								{formatCurrency(row.tax)}
							</Td>
							<Td property="total">
								{formatCurrency(row.total)}
							</Td>
							<Td property="shipTo">
								{row.shipTo}
							</Td>
							<Td property="salesPerson[name]">
								{row.salesPerson?.name ?? 'N/A'}
							</Td>
							<Td property="store[id]">
								{row.store?.id ?? 'N/A'}
							</Td>
							<Td property="receivedPercent">
								{row.receivedPercent}
							</Td>
							<Td
								property="status"
								class="text-nowrap text-{getPOStatus(row).textColor}"
							>
								{row.status}
							</Td>
							<Td property="internalComments">
								{row.internalComments}
							</Td>
						</tr>
					{/snippet}
				</Table>
			{/if}
		</div>
		<div class="card-footer">
			<Button
				outline
				size="sm"
				iconClass="print"
				disabled={!selectedPurchaseOrder || isLoadingPurchaseOrders}
				onclick={() => {
					if (selectedPurchaseOrder) {
						reportJobModalComponent.open({
							type: 'Purchase Order',
							parameters: {
								purchaseorderid: selectedPurchaseOrder.purchaseOrderId.toString(),
								storeid: selectedPurchaseOrder.store.id.toString(),
							},
						})
					}
				}}
			>
				{translate('common:print', 'Print')}...
			</Button>
		</div>
	</div>
	{#if !isLoadingPurchaseOrders}
		<div class="card mt-3">
			<div class="card-header">
				<h4 class="mb-0">
					{translate('common:purchaseOrder', 'Purchase Order')}
					{selectedPurchaseOrder ? `#${selectedPurchaseOrder.id}` : ''}
				</h4>
			</div>
			<div class="card-body p-1">
				{#if !selectedPurchaseOrder}
					<div class="text-center p-3">
						<h5>
							{translate('vendor.selectAPurchaseOrderToViewLineItems', 'Select a purchase order to view line items')}
						</h5>
					</div>
				{:else}
					<Table
						responsive
						columns={lineItemColumn}
						rows={selectedPurchaseOrder.lines}
					>
						{#snippet noRows({ visibleColumnsCount })}
							<tr>
								<td
									class="text-center"
									colspan={visibleColumnsCount}
								>
									{translate(
										'vendor.noLineItemsFoundForTheSelectedPurchaseOrder',
										'No line items found for the selected purchase order',
									)}
								</td>
							</tr>
						{/snippet}
						{#snippet children({ row })}
							<tr>
								<Td property="lookup">
									{row.lookup}
								</Td>
								<Td property="quantity">
									{row.quantity}
								</Td>
								<Td property="quantityReceived">
									{row.quantityReceived}
								</Td>
								<Td property="description">
									{row.description}
								</Td>
								<Td property="price">
									{formatCurrency(row.price)}
								</Td>
								<Td property="total">
									{formatCurrency(row.total)}
								</Td>
							</tr>
						{/snippet}
					</Table>
				{/if}
			</div>
		</div>
	{/if}
{/if}

<ReportJobModal bind:this={reportJobModalComponent} />
